import React, { createContext, useContext, useEffect, useReducer, useCallback } from 'react';
import axios from '../api/axios';
import { useUser } from './UserContext';
import { useSelector } from 'react-redux';
import { GetClientType } from '../config';
import { notification ,Modal} from 'antd';
import { dir, t } from 'i18next';
import getProfileType from '../helpers/getProfileType';

const DownloadManagerContext = createContext();

const clone = (obj)=> {
    if (null == obj || "object" != typeof obj) return obj;
    var copy = obj.constructor();
    for (var attr in obj) {
        if (obj.hasOwnProperty(attr)) copy[attr] = obj[attr];
    }
    return copy;
}
const lang = localStorage.getItem('lang')

export const useDownloadManager = () => useContext(DownloadManagerContext);

const DownloadStates = {
    NotDownloaded: 0,
    Downloading: 1,
    Finished: 2,
    Paused: 3,
    Failed: 4
};

const initialState = {
    downloadList: {},  // Change from array to object
    downloadListChanged: 0
};

const reducer = (state, action) => {
    switch (action.type) {
        case 'UPDATE_DOWNLOAD': {
            return { 
                ...state, 
                downloadList: { ...state.downloadList, ...action.payload }, 
                downloadListChanged: state.downloadListChanged + 1 
            };
        }
        default:
            return state;
    }
};


export const DownloadManagerProvider = ({ children }) => {

    const notificationDirection = lang === 'fa' || lang === 'ar' ? 'rtl' : 'ltr';
    const [state, dispatch] = useReducer(reducer, initialState);
    let partialData = {};

    const {isAuthenticated} = useSelector((state) => state.initialize);
    const license = useSelector((state) => state?.profile?.profileData?.license);


    const getFileName = (product) => product.name;

    const getFullFileName = (product) => {
        const clientType = GetClientType()
        if (clientType == "revit") {
            return product.name + '.zip';
        }
        const ext = 'zip';
        if(getProfileType(product)=="texture"){
            const data = btoa(JSON.stringify({ title: product.name, category: 'texture', filename: product.name + '.zip' }));
            return `${data}@.${ext}`;


        }else{
            const data = btoa(JSON.stringify({ title: product.name, category: 'assets', filename: product.name + '.zip' }));
            return `${data}@.${ext}`;

        }
    };
    const updateDownloadState = useCallback((product, progress, cancelTokenSource) => {
        setTimeout(() => {
            dispatch({
                type: 'UPDATE_DOWNLOAD',
                payload: {
                    [product.id]: {
                        ...product,
                        progress,
                        downloadStatus: DownloadStates.Downloading,
                        cancelTokenSource
                    }
                }
            });
        }, 300); // Adjust this value to balance real-time updates and performance
    }, []);
    
    

    const saveToStorage = (key, data) => localStorage.setItem(key, JSON.stringify(data));
    const getFromStorage = (key) => JSON.parse(localStorage.getItem(key)) || [];

    const handleDownload = (product,indexAssets=0) => {
        if(indexAssets===false){
            Modal.error({
                title: t("messages.error"),
                 content: t("messages.selected_color_first"),
                
            });
            return
        }

     
        if (!isAuthenticated) {
            notification.error({
                message: t('download.login_alert_title_download'),
                description: t('download.login_alert_decription_download'),
                placement: "topRight",
                style: { direction: notificationDirection }
            });
            return
    
        }

        if ((license?.is_expired || !license) && !product.is_free) {
            notification.error({
                message: t('download.login_alert_title_download'),
                description: t('download.free_alert_decription_download'),
                placement: "topRight",
                style: { direction: notificationDirection }
            });
            return
        }
            switch (checkDownloadState(product)) {
                case DownloadStates.NotDownloaded:
                case DownloadStates.Failed:
                case DownloadStates.Finished:
                    download(product,indexAssets);
                    break;
                case DownloadStates.Downloading:
                    pause(product,indexAssets);
                    break;
                case DownloadStates.Paused:
                    resume(product,indexAssets);
                    break;
            }
    };

    const checkDownloadState = (product) => {
        const storedDownloads = getFromStorage('downloads');
        if (storedDownloads.some(p => p.id === product.id)) {
            return DownloadStates.Finished;
        }
    
        return state.downloadList[product.id] ? DownloadStates.Downloading : DownloadStates.NotDownloaded;
    };
    
    const getDownloadProgress = useCallback((product) => {
        return state.downloadList[product.id]?.progress || 0;
    }, [state.downloadList]);
    

    const download = async (product,indexAssets) => {
       
      
        
        try {
            const cancelTokenSource = new AbortController();
            let downloads = getFromStorage('downloads')
            let updatedDownloads = downloads.filter(p => p.id !== product.id)
            saveToStorage('downloads', updatedDownloads );

            updateDownloadState(product, 0, cancelTokenSource);
            const response = await axios.get(`${product?.assets[indexAssets]?.download_url}?t=${Date.now()}`, {
                responseType: 'blob',
                   onDownloadProgress: (e) => updateDownloadState(product, Math.round((e.loaded * 100) / e.total), cancelTokenSource),
                  signal: cancelTokenSource.signal,
            });

            const blob = new Blob([response.data]);
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', getFullFileName(product));
            document.body.appendChild(link);
            link.click();

            saveToStorage('downloads', [...getFromStorage('downloads'), product]);
        } catch (error) {
            console.error(error);
        }
    };

    const pause = (product) => {
        if (state.downloadList[product.id]?.cancelTokenSource) {
            state.downloadList[product.id].cancelTokenSource.abort();
        }
    
        dispatch({
            type: 'UPDATE_DOWNLOAD',
            payload: {
                [product.id]: {
                    ...state.downloadList[product.id],
                    downloadStatus: DownloadStates.NotDownloaded
                }
            }
        });
    };
    

    const resume = (product,indexAssets) => download(product,indexAssets);

    return (
        <DownloadManagerContext.Provider value={{ handleDownload, DownloadStates, ...state, checkDownloadState, getDownloadProgress }}>
            {children}
        </DownloadManagerContext.Provider>
    );
};
